import { useState, useEffect } from 'react';
import { useToast } from '../components/ui/toast';
import { rolesAPI, UpdateRoleRequest } from '../services/api';
import { useAuthStore } from '../stores';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  ChevronLeft,
  ChevronRight,
  Trash2,
  Edit,
  MoreHorizontal,
  Shield,
  Loader2
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

interface Role {
  id: string;
  name: string;
  permissions: string[];
  created_at: string;
}

const AVAILABLE_PERMISSIONS = [
  { value: 'users.manage', label: 'Manage Users', description: 'Create, edit, and delete users' }
];

const Roles: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    name: '',
    permissions: [] as string[],
  });

  const { addToast } = useToast();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await rolesAPI.list();
      setRoles(response.data.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      addToast({
        title: "Error",
        description: "Failed to load roles. Please refresh the page.",
        variant: "destructive"
      });
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      addToast({
        title: "Validation Error",
        description: "Please enter a role name.",
        variant: "destructive",
      });
      return;
    }

    setCreateLoading(true);

    try {
      const response = await rolesAPI.create({
        name: formData.name,
        permissions: formData.permissions,
      });
      
      const newRole = response.data.data.role;
      setRoles([newRole, ...roles]);
      setFormData({ name: '', permissions: [] });
      setShowCreateDialog(false);
      
      addToast({
        title: "Role Created",
        description: "Role has been created successfully.",
        variant: "success"
      });
    } catch (error: any) {
      console.error('Failed to create role:', error);
      addToast({
        title: "Creation Failed",
        description: error.response?.data?.message || "Failed to create role. Please try again.",
        variant: "destructive"
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roleToEdit) return;

    if (!formData.name.trim()) {
      addToast({
        title: "Validation Error",
        description: "Please enter a role name.",
        variant: "destructive",
      });
      return;
    }

    setUpdateLoading(true);

    try {
      const updateData: UpdateRoleRequest = {
        name: formData.name,
        permissions: formData.permissions,
      };

      const response = await rolesAPI.update(roleToEdit.id, updateData);
      
      const updatedRole = response.data.data.role;
      setRoles(roles.map(r => r.id === updatedRole.id ? updatedRole : r));
      setShowEditDialog(false);
      setRoleToEdit(null);
      
      addToast({
        title: "Role Updated",
        description: "Role has been updated successfully.",
        variant: "success"
      });
    } catch (error: any) {
      console.error('Failed to update role:', error);
      addToast({
        title: "Update Failed",
        description: error.response?.data?.message || "Failed to update role. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    
    setDeleteLoading(true);
    try {
      await rolesAPI.delete(roleToDelete.id);
      setRoles(roles.filter(r => r.id !== roleToDelete.id));
      setShowDeleteDialog(false);
      setRoleToDelete(null);
      
      addToast({
        title: "Role Deleted",
        description: "Role has been deleted successfully.",
        variant: "success"
      });
    } catch (error) {
      console.error('Failed to delete role:', error);
      addToast({
        title: "Delete Failed",
        description: "Failed to delete role. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEditDialog = (role: Role) => {
    setRoleToEdit(role);
    setFormData({
      name: role.name,
      permissions: role.permissions || [],
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (role: Role) => {
    setRoleToDelete(role);
    setShowDeleteDialog(true);
  };

  const togglePermission = (permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const totalPages = Math.ceil(roles.length / itemsPerPage);
  const paginatedRoles = roles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const hasPermission = useAuthStore((state) => state.hasPermission);

  if (!hasPermission('users.manage')) {
    return (
      <Layout title="Access Denied" description="You don't have permission to access this page">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">Access Denied</h3>
            <p className="text-muted-foreground">
              You don't have permission to manage roles.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title="Roles" 
      description="Manage user roles and permissions"
    >
      <TooltipProvider>
        <div className="mobile-padding py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
        <div className="flex justify-end items-center">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger 
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              onClick={() => {
                setFormData({ name: '', permissions: [] });
                setShowCreateDialog(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Create Role
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-hidden p-0">
              <form onSubmit={handleCreateRole} className="flex flex-col max-h-[85vh]">
                <div className="px-6 pt-6 pb-4 flex-shrink-0">
                  <DialogHeader className="space-y-3">
                    <DialogTitle>Create New Role</DialogTitle>
                    <DialogDescription>
                      Add a new role with specific permissions
                    </DialogDescription>
                  </DialogHeader>
                </div>
                
                <div className="flex-1 overflow-y-auto px-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                    {/* Left Column - Role Name */}
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <Label htmlFor="name" className="text-sm font-medium">Role Name</Label>
                        <Input
                          id="name"
                          placeholder="e.g., Administrator, Editor"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Right Column - Permissions */}
                    <div className="space-y-5 md:border-l md:pl-6">
                      <div>
                        <Label className="text-sm font-semibold">Permissions</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Select role permissions
                        </p>
                      </div>
                      <div className="space-y-4">
                        {AVAILABLE_PERMISSIONS.map((perm) => (
                          <div key={perm.value} className="flex items-start space-x-3">
                            <Checkbox
                              id={`create-${perm.value}`}
                              checked={formData.permissions.includes(perm.value)}
                              onCheckedChange={() => togglePermission(perm.value)}
                              className="mt-1"
                            />
                            <div className="grid gap-1 leading-none flex-1">
                              <label
                                htmlFor={`create-${perm.value}`}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                              >
                                {perm.label}
                              </label>
                              <p className="text-sm text-muted-foreground leading-relaxed">
                                {perm.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="px-6 pb-6 pt-4 border-t flex-shrink-0">
                  <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createLoading}>
                      {createLoading ? 'Creating...' : 'Create Role'}
                    </Button>
                  </DialogFooter>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-hidden p-0">
            <form onSubmit={handleUpdateRole} className="flex flex-col max-h-[85vh]">
              <div className="px-6 pt-6 pb-4 flex-shrink-0">
                <DialogHeader className="space-y-3">
                  <DialogTitle>Edit Role</DialogTitle>
                  <DialogDescription>
                    Update role information and permissions
                  </DialogDescription>
                </DialogHeader>
              </div>
              
              <div className="flex-1 overflow-y-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                  {/* Left Column - Role Name */}
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <Label htmlFor="edit_name" className="text-sm font-medium">Role Name</Label>
                      <Input
                        id="edit_name"
                        placeholder="e.g., Administrator, Editor"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Right Column - Permissions */}
                  <div className="space-y-5 md:border-l md:pl-6">
                    <div>
                      <Label className="text-sm font-semibold">Permissions</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Select role permissions
                      </p>
                    </div>
                    <div className="space-y-4">
                      {AVAILABLE_PERMISSIONS.map((perm) => (
                        <div key={perm.value} className="flex items-start space-x-3">
                          <Checkbox
                            id={`edit-${perm.value}`}
                            checked={formData.permissions.includes(perm.value)}
                            onCheckedChange={() => togglePermission(perm.value)}
                            className="mt-1"
                          />
                          <div className="grid gap-1 leading-none flex-1">
                            <label
                              htmlFor={`edit-${perm.value}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {perm.label}
                            </label>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {perm.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-6 pb-6 pt-4 border-t flex-shrink-0">
                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateLoading}>
                    {updateLoading ? 'Updating...' : 'Update Role'}
                  </Button>
                </DialogFooter>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="sm:max-w-[450px] max-h-[85vh] overflow-hidden p-0">
            <div className="flex flex-col max-h-[85vh]">
              <div className="px-6 pt-6 pb-6 flex-shrink-0">
                <DialogHeader className="space-y-4">
                  <DialogTitle>Delete Role</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this role? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
              </div>
              
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                {roleToDelete && (
                  <div className="bg-muted p-5 rounded-lg space-y-3">
                    <h4 className="font-medium text-base">{roleToDelete.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {roleToDelete.permissions.length} permission(s)
                    </p>
                  </div>
                )}
              </div>
              
              <div className="px-6 pb-6 pt-5 border-t flex-shrink-0">
                <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading} className="w-full sm:w-auto">
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleDeleteRole} disabled={deleteLoading} className="w-full sm:w-auto">
                    {deleteLoading ? 'Deleting...' : 'Delete Role'}
                  </Button>
                </DialogFooter>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-sm text-muted-foreground">Loading roles...</p>
              </div>
            ) : roles.length === 0 ? (
              <div className="text-center py-12">
                <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium mb-2">No roles found</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first role to get started
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Role
                </Button>
              </div>
            ) : (
              <>
                <div className="hidden md:block rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Permissions</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedRoles.map((role) => (
                        <TableRow key={role.id}>
                          <TableCell className="font-medium">
                            {role.name}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {role.permissions.length > 0 ? (
                                role.permissions.map((perm) => (
                                  <Badge key={perm} variant="secondary" className="text-xs">
                                    {perm}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-sm text-muted-foreground">No permissions</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(role.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditDialog(role)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Edit Role
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openDeleteDialog(role)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Delete Role
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="md:hidden divide-y divide-border">
                  {paginatedRoles.map((role) => (
                    <div key={role.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base truncate">
                            {role.name}
                          </h3>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {role.permissions.length > 0 ? (
                              role.permissions.map((perm) => (
                                <Badge key={perm} variant="secondary" className="text-xs">
                                  {perm}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">No permissions</span>
                            )}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-9 w-9 p-0 touch-target flex-shrink-0"
                            >
                              <MoreHorizontal className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(role)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Role
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => openDeleteDialog(role)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Role
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        Created {formatDate(role.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, roles.length)} of {roles.length} entries
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="touch-target"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline ml-1">Previous</span>
                      </Button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className="w-9 h-9 p-0 touch-target"
                          >
                            {page}
                          </Button>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="touch-target"
                      >
                        <span className="hidden sm:inline mr-1">Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        </div>
      </TooltipProvider>
    </Layout>
  );
};

export default Roles;
